/**
 * 报告预览视图组件 — admin 配置页 / admin 查看页 / 老师查看页 三处共用。
 *
 * 数据加载策略由调用方注入（loadPreview / loadSchools / loadClasses），
 * 支持：
 *   - 不同的后端 endpoint（admin vs teacher）
 *   - 老师权限限制（lockedScope：班主任只能看自己的班级，下拉选项也只列那些班）
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import {
  PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import {
  ReportPreviewResponse, PlaceholderValueDto, ChartDataItem,
  ScopeSchool, ScopeClass, SchoolStack, WarningRow,
} from '../services/reportService';
import { exportReportPdf, exportReportWord } from '../utils/reportExport';

const ReportPieChart: React.FC<{ data: ChartDataItem[] }> = ({ data }) => (
  <ResponsiveContainer width="100%" height={280}>
    <PieChart>
      <Pie
        data={data}
        cx="50%" cy="50%" outerRadius={90}
        dataKey="count" nameKey="name"
        label={(props: any) => `${props.name} ${props.payload?.percentage ?? ''}%`}
      >
        {data.map((entry, index) => (
          <Cell key={index} fill={entry.color || `hsl(${index * 60}, 65%, 55%)`} />
        ))}
      </Pie>
      <Tooltip formatter={(value: any, name: any) => [`${value}人`, name]} />
      <Legend />
    </PieChart>
  </ResponsiveContainer>
);

const ReportBarChart: React.FC<{ data: ChartDataItem[] }> = ({ data }) => (
  <ResponsiveContainer width="100%" height={280}>
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip formatter={(value: any) => [`${value}人`, '人数']} />
      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
        {data.map((entry, index) => (
          <Cell key={index} fill={entry.color || `hsl(${index * 60}, 65%, 55%)`} />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

/**
 * 预警名单表：逐学生一行，列出各分项预警分 + 预警指数。
 * 已由后端筛选(预警指数≥3)并按预警指数降序。分值 3=红、1=橙、0=灰。
 */
const WarningListTable: React.FC<{ data: WarningRow[] }> = ({ data }) => {
  if (!data || data.length === 0) {
    return <p style={{ textAlign: 'center', color: '#64748b', padding: '24px' }}>本校暂无达到预警标准（预警指数≥3）的学生。</p>;
  }
  const cell = (v: number) => (
    <td style={{
      border: '1px solid #cbd5e1', padding: '4px 8px', textAlign: 'center',
      color: v === 3 ? '#b91c1c' : v === 1 ? '#c2410c' : '#94a3b8',
      fontWeight: v > 0 ? 700 : 400,
    }}>{v}</td>
  );
  const th = { border: '1px solid #cbd5e1', padding: '6px 8px', background: '#eaf3f4', fontWeight: 600 } as const;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 13, margin: '8px auto', minWidth: 640 }}>
        <thead>
          <tr>
            <th style={th}>唯一编码</th>
            <th style={th}>抑郁</th><th style={th}>焦虑</th><th style={th}>自伤风险</th>
            <th style={th}>受欺凌</th><th style={th}>学业适应风险</th><th style={th}>学校环境风险</th>
            <th style={{ ...th, background: '#dbeef0' }}>预警指数</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r, i) => (
            <tr key={i}>
              {/* 唯一编码：学生档案 name 字段存的即编码；缺失回退 code(学号/A+答卷id) */}
              <td style={{ border: '1px solid #cbd5e1', padding: '4px 8px', textAlign: 'center', fontWeight: 600 }}>{r.name && r.name !== '—' ? r.name : r.code}</td>
              {cell(r.depression)}{cell(r.anxiety)}{cell(r.selfHarm)}
              {cell(r.bullying)}{cell(r.academic)}{cell(r.environment)}
              <td style={{ border: '1px solid #cbd5e1', padding: '4px 8px', textAlign: 'center', fontWeight: 700, background: '#f8fafc' }}>{r.warningIndex}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ textAlign: 'center', color: '#64748b', fontSize: 12, marginTop: 4 }}>
        共 {data.length} 名学生进入预警（预警指数≥3，按预警指数从大到小排序）。注：预警指数越高表明个体心理健康与学业适应及学校环境风险越高。
      </p>
    </div>
  );
};

/** 横轴学校名 tag 标签：水平排列的小圆角标签，本校高亮主色。 */
const SchoolTagTick: React.FC<any> = ({ x, y, payload, selfMap }) => {
  const name: string = payload?.value ?? '';
  const self: boolean = !!selfMap?.[name];
  const w = Math.max(36, name.length * 13 + 12);
  return (
    <g transform={`translate(${x},${y + 10})`}>
      <rect x={-w / 2} y={0} width={w} height={20} rx={10}
        fill={self ? '#4DA1A9' : '#EAF3F4'} stroke={self ? '#2E7A93' : '#D3E5E7'} strokeWidth={1} />
      <text x={0} y={14} textAnchor="middle" fontSize={11}
        fill={self ? '#ffffff' : '#456'} fontWeight={self ? 700 : 500}>{name}</text>
    </g>
  );
};

/** 分学校对比柱状图：横轴=学校简称(tag)；平均分图柱高=value，发生率图=percentage；校级高亮本校。 */
const ReportBySchoolBarChart: React.FC<{ data: ChartDataItem[] }> = ({ data }) => {
  const isAvg = data.some(d => d.value != null);
  const dataKey = isAvg ? 'value' : 'percentage';
  const unit = isAvg ? '分' : '%';
  const hasSelf = data.some(d => d.self);
  // 横轴用简称(tag)，tooltip 用全名
  const rows = data.map(d => ({ ...d, tag: d.shortName || d.name, full: d.name }));
  const selfMap: Record<string, boolean> = {};
  rows.forEach(r => { selfMap[r.tag] = !!r.self; });
  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={rows} margin={{ bottom: 40, top: 8 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="tag" interval={0} height={44} tickLine={false} axisLine={{ stroke: '#D3E5E7' }}
          tick={<SchoolTagTick selfMap={selfMap} />} />
        <YAxis tick={{ fontSize: 11 }} width={44} />
        <Tooltip
          formatter={(value: any) => [`${value}${unit}`, isAvg ? '平均分' : '发生率']}
          labelFormatter={(_l: any, p: any) => p?.[0]?.payload?.full || _l} />
        <Bar dataKey={dataKey} radius={[6, 6, 0, 0]} maxBarSize={48}>
          {rows.map((entry, index) => (
            // 系统主色系(品牌色 #4DA1A9)：校级高亮本校(主色)，其他校淡一档；市级统一主色
            <Cell key={index} fill={hasSelf ? (entry.self ? '#4DA1A9' : '#BFDCDF') : '#4DA1A9'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * 堆叠图自定义图例：按传入的档位顺序(轻→重)横向等距排列、整体居中对齐。
 * 替代 Recharts 默认 Legend（默认按 Bar 声明顺序、item 间距不固定，导出后看着没拉齐）。
 */
const TierLegend: React.FC<{ labels: string[]; colors: Record<string, string> }> = ({ labels, colors }) => (
  <div style={{
    display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center',
    gap: '8px 24px', padding: '8px 4px 0',
  }}>
    {labels.map((label) => (
      <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>
        <span style={{ width: 12, height: 12, borderRadius: 3, background: colors[label] || '#94a3b8', flex: '0 0 auto' }} />
        {label}
      </span>
    ))}
  </div>
);

/**
 * 分学校堆叠柱状图：每校一根堆叠柱，柱内各段=该指标「有问题」档位占全校比例%
 * （「无/健康」档已在后端剔除，故柱高=有问题人群总占比，可跨校直接比高低）。
 * 横轴=学校简称(tag 样式)，Y=百分比(0-100)，图例=档位，渐变蓝(轻→重 浅→深)。校级本校柱描边高亮。
 */
const ReportBySchoolStackChart: React.FC<{ data: SchoolStack[] }> = ({ data }) => {
  // 档位按 order(规则 orderNum) 严格升序，避免某校缺档(如中段0人)打乱顺序。
  // 升序渲染 → 轻/低在底、重/高在顶(Recharts 先画的 Bar 在底部)。
  const tierColors: Record<string, string> = {};
  const tierOrder: Record<string, number> = {};
  data.forEach((s) =>
    s.segments.forEach((seg) => {
      tierColors[seg.label] = seg.color;
      tierOrder[seg.label] = seg.order;
    }),
  );
  const tierLabels: string[] = Object.keys(tierOrder).sort((a, b) => tierOrder[a] - tierOrder[b]);

  // 转成 Recharts 行：{ name(简称), full, total, self, [档位]: 占比% }
  const rows = data.map((s) => {
    const row: any = { name: s.shortName || s.school, full: s.school, total: s.total, self: s.self };
    s.segments.forEach((seg) => { row[seg.label] = seg.percentage; });
    return row;
  });
  const selfMap: Record<string, boolean> = {};
  rows.forEach((r) => { selfMap[r.name] = r.self; });
  const hasSelf = data.some((s) => s.self);

  return (
    <ResponsiveContainer width="100%" height={360}>
      {/* right 边距给"太矮段的柱右侧百分比标签"留位置，避免最右柱的外侧标签被裁 */}
      <BarChart data={rows} margin={{ bottom: 40, top: 8, right: 30 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="name" interval={0} height={44} tickLine={false} axisLine={{ stroke: '#D3E5E7' }}
          tick={<SchoolTagTick selfMap={selfMap} />} />
        <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} allowDecimals={false}
          tickFormatter={(v: number) => `${v}%`} tick={{ fontSize: 11 }} width={44} />
        <Tooltip
          formatter={(value: any, label: any) => [`${value}%`, label]}
          labelFormatter={(_l: any, p: any) => p?.[0]?.payload?.full || _l} />
        {/* 自定义图例：按档位严格升序(轻→重)、等距对齐居中，避免默认 Legend 顺序错乱/间距不齐 */}
        <Legend content={<TierLegend labels={tierLabels} colors={tierColors} />} />
        {tierLabels.map((label, i) => (
          <Bar
            key={label}
            dataKey={label}
            stackId="a"
            fill={tierColors[label] || `hsl(${200 + i * 12}, 45%, ${65 - i * 8}%)`}
            radius={i === tierLabels.length - 1 ? [6, 6, 0, 0] : undefined}
            maxBarSize={56}
          >
            {/* 校级报告：非本校的柱描边透明、本校加深色描边以突出 */}
            {hasSelf &&
              rows.map((r, idx) => (
                <Cell
                  key={idx}
                  stroke={r.self ? '#1f2937' : 'transparent'}
                  strokeWidth={r.self ? 1.5 : 0}
                />
              ))}
            {/* 段内百分比标签：每段都显示（校方只看 PDF，比例必须全可见——需求修改单第1条）。
                段高够放字 → 居中白/深字；太矮 → 放到柱右侧深字。
                同一根柱里多个"太矮段"的外侧标签按堆叠位置上下错开，避免相互重叠 */}
            <LabelList dataKey={label} content={(props: any) => {
              const { x, y, width, height, value, index } = props;
              const v = typeof value === 'number' ? value : Number(value);
              if (!v || width == null || height == null) return null;
              const dark = i >= Math.ceil(tierLabels.length / 2); // 后半段颜色较深 → 白字
              if (height >= 12) {
                return (
                  <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="central"
                    fontSize={11} fontWeight={600} fill={dark ? '#ffffff' : '#334155'}>
                    {v.toFixed(1)}%
                  </text>
                );
              }
              // 外侧标签防撞：找出本柱所有"太矮"档（按堆叠自下而上），把各标签沿垂直方向散开
              const row = rows[index] || {};
              const pxPerPct = height / v; // 本图 1% 对应的像素高
              const smalls = tierLabels.filter((t) => {
                const tv = Number(row[t]);
                return tv > 0 && tv * pxPerPct < 12;
              });
              const k = smalls.indexOf(label);
              const n = smalls.length;
              const offsetY = k >= 0 && n > 1 ? ((n - 1) / 2 - k) * 12 : 0; // 下面的往下、上面的往上
              return (
                <text x={x + width + 4} y={y + height / 2 + offsetY} textAnchor="start" dominantBaseline="central"
                  fontSize={10} fontWeight={600} fill="#334155">
                  {v.toFixed(1)}%
                </text>
              );
            }} />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export interface PreviewLoaders {
  loadPreview: (scope: { schoolId?: number; classId?: string }) => Promise<ReportPreviewResponse>;
  loadSchools: () => Promise<ScopeSchool[]>;
  loadClasses: (schoolId: number) => Promise<ScopeClass[]>;
}

interface Props extends PreviewLoaders {
  /** 顶部展示的标题（一般是 template.title 或问卷标题） */
  title?: string;
  /** 由父级控制 scope；传了就不显示组件内自己的下拉，scope 完全跟随 props */
  controlledScope?: { schoolId?: number; classId?: string };
  /** 老师 scope 锁定：传了就只能在 lockedScope 内切换 */
  lockedScope?: {
    /** 仅允许这些学校（null = 不限） */
    schoolIds?: number[];
    /** 仅允许这些班级（null = 不限） */
    classIds?: string[];
    /** 默认 scope（进入即应用） */
    defaultSchoolId?: number;
    /** 默认 classId */
    defaultClassId?: string;
    /** 是否可以选"全部学校"（校管理员=true，班主任=false） */
    allowAllSchools?: boolean;
    /** 是否可以选"全部班级"（校管理员=true，班主任=false 的话强制选某个班） */
    allowAllClasses?: boolean;
  };
  /** 没有报告模板时显示的回退文案 */
  emptyHint?: string;
}

const ReportPreviewView: React.FC<Props> = ({
  loadPreview, loadSchools, loadClasses,
  title, lockedScope, controlledScope, emptyHint = '该问卷暂未配置报告模板',
}) => {
  const [previewData, setPreviewData] = useState<ReportPreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [schools, setSchools] = useState<ScopeSchool[]>([]);
  const [classes, setClasses] = useState<ScopeClass[]>([]);
  const [schoolId, setSchoolId] = useState<number | null>(lockedScope?.defaultSchoolId ?? null);
  const [classId, setClassId] = useState<string | null>(lockedScope?.defaultClassId ?? null);

  const previewRef = useRef<HTMLDivElement>(null);
  const chartRootsRef = useRef<Map<string, any>>(new Map());
  const [exporting, setExporting] = useState<'pdf' | 'word' | null>(null);
  // 首次加载由 init effect 按 controlledScope 处理；controlledScope effect 跳过挂载首跑，
  // 只在之后 scope 真变化时 reload，避免挂载时两个 effect 竞态覆盖(filter 显示某校/内容全市)。
  const didInitScopeRef = useRef(false);

  const allowAllSchools = lockedScope?.allowAllSchools ?? true;
  const allowAllClasses = lockedScope?.allowAllClasses ?? true;

  // 过滤后的学校选项（按 lockedScope.schoolIds）
  const visibleSchools = lockedScope?.schoolIds
    ? schools.filter(s => lockedScope.schoolIds!.includes(s.schoolId))
    : schools;

  const visibleClasses = lockedScope?.classIds
    ? classes.filter(c => lockedScope.classIds!.includes(c.classId))
    : classes;

  // 拉数据
  const reload = useCallback(async (sId: number | null, cId: string | null) => {
    setLoading(true);
    setError('');
    try {
      const data = await loadPreview({
        schoolId: sId ?? undefined,
        classId: cId ?? undefined,
      });
      setPreviewData(data);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || '加载报告失败');
    } finally {
      setLoading(false);
    }
  }, [loadPreview]);

  // 初始化：拉学校列表 + 首次预览
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s] = await Promise.all([loadSchools()]);
        if (cancelled) return;
        setSchools(s);

        // 班主任：默认有 defaultSchoolId 直接拉对应班级
        if (lockedScope?.defaultSchoolId) {
          const cls = await loadClasses(lockedScope.defaultSchoolId);
          if (cancelled) return;
          setClasses(cls);
        }

        // controlledScope 模式(如统计分析页从指标 tab 切过来带着已选学校)：
        // 首次加载必须用父级传入的 scope，否则会先按全市加载、再被下面的 controlledScope
        // effect 覆盖，造成「filter 显示某校、内容却是全市」的竞态 bug。
        // 优先级：controlledScope > lockedScope 默认值。
        const initSchoolId = controlledScope
          ? (controlledScope.schoolId ?? null)
          : (lockedScope?.defaultSchoolId ?? null);
        const initClassId = controlledScope
          ? (controlledScope.classId ?? null)
          : (lockedScope?.defaultClassId ?? null);
        // 同步内部 state，保证 filter 显示与实际加载一致
        setSchoolId(initSchoolId);
        setClassId(initClassId);
        await reload(initSchoolId, initClassId);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message || '加载失败');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // controlledScope 模式：父级 scope 变化 → reload。
  // 跳过挂载首跑（init effect 已按 controlledScope 加载），只在后续 scope 真变化时 reload。
  useEffect(() => {
    if (!controlledScope) return;
    if (!didInitScopeRef.current) { didInitScopeRef.current = true; return; }
    const s = controlledScope.schoolId ?? null;
    const c = controlledScope.classId ?? null;
    setSchoolId(s);
    setClassId(c);
    reload(s, c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledScope?.schoolId, controlledScope?.classId]);

  // 切学校（仅在非 controlledScope 模式下使用）
  const onSchoolChange = async (s: number | null) => {
    setSchoolId(s);
    setClassId(null);
    if (s === null) {
      setClasses([]);
      reload(null, null);
      return;
    }
    try {
      const cls = await loadClasses(s);
      setClasses(cls);
    } catch (e) { console.error(e); }
    reload(s, null);
  };

  const onClassChange = (c: string | null) => {
    setClassId(c);
    reload(schoolId, c);
  };

  // 导出文件名：报告标题 + 学校简称（校级取下拉里选中的校名）+ 日期
  const exportFilename = useCallback(() => {
    const base = (title ?? '大报告').replace(/[\\/:*?"<>|]/g, '');
    const sName = schoolId
      ? (schools.find((s) => s.schoolId === schoolId)?.schoolName || '本校').replace(/[\\/:*?"<>|]/g, '')
      : '全市';
    const d = new Date();
    const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    return `${base}_${sName}_${stamp}`;
  }, [title, schoolId, schools]);

  const handleExport = async (kind: 'pdf' | 'word') => {
    if (!previewRef.current || exporting) return;
    setExporting(kind);
    try {
      const fn = exportFilename();
      if (kind === 'pdf') await exportReportPdf(previewRef.current, fn);
      else await exportReportWord(previewRef.current, fn);
    } catch (e) {
      console.error('导出失败', e);
      setError('导出失败，请重试');
    } finally {
      setExporting(null);
    }
  };

  // 渲染 HTML + 图表
  const renderToDom = useCallback((data: ReportPreviewResponse) => {
    if (!previewRef.current) return;
    chartRootsRef.current.forEach((root) => { try { root.unmount(); } catch {} });
    chartRootsRef.current.clear();

    const valueMap = new Map<string, PlaceholderValueDto>();
    data.placeholderValues.forEach((pv) => valueMap.set(pv.key, pv));

    // 第一遍：把所有 {{key}} 文本占位符替换成带 data-placeholder-key 的 span
    // （兼容用户手写 HTML 或 seed 脚本生成的模板，没有 data 属性）
    let html = data.templateHtml;
    valueMap.forEach((_pv, key) => {
      const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, 'g');
      html = html.replace(re,
        `<span data-placeholder-key="${key}"></span>`);
    });

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    tempDiv.querySelectorAll('[data-placeholder-key]').forEach((el) => {
      const key = el.getAttribute('data-placeholder-key');
      if (!key) return;
      const pv = valueMap.get(key);
      if (!pv) {
        el.innerHTML = '<span style="color:#ef4444;">[未找到数据]</span>';
        return;
      }
      if (pv.placeholderType === 'TEXT') {
        const span = document.createElement('span');
        span.className = 'report-value';
        span.textContent = pv.resolvedValue || 'N/A';
        // 口径脚注：占比/平均分等数字后紧跟一个灰色小字，说明计算口径（导出 PDF/Word 一并带上）
        if (pv.basis) {
          const wrap = document.createElement('span');
          wrap.appendChild(span);
          const note = document.createElement('span');
          note.className = 'report-basis';
          note.style.cssText = 'color:#94a3b8;font-size:0.78em;margin-left:2px;white-space:nowrap;';
          note.textContent = `（${pv.basis}）`;
          wrap.appendChild(note);
          el.replaceWith(wrap);
          return;
        }
        el.replaceWith(span);
      } else {
        const c = document.createElement('div');
        c.className = 'report-chart-container';
        c.setAttribute('data-chart-key', key);
        c.setAttribute('data-chart-type', pv.placeholderType);
        c.style.cssText = 'width:100%;min-height:300px;margin:16px 0;padding:16px;background:#f8fafc;border-radius:12px;border:1px solid #e2e8f0;';
        el.replaceWith(c);
      }
    });

    previewRef.current.innerHTML = tempDiv.innerHTML;

    previewRef.current.querySelectorAll('.report-chart-container').forEach((container) => {
      const key = container.getAttribute('data-chart-key');
      const chartType = container.getAttribute('data-chart-type');
      if (!key) return;
      const pv = valueMap.get(key);
      if (!pv) {
        container.innerHTML = '<p style="text-align:center;color:#94a3b8;padding:40px;">暂无数据</p>';
        return;
      }
      // 预警名单表
      if (chartType === 'WARNING_LIST_TABLE') {
        const root = createRoot(container);
        chartRootsRef.current.set(key, root);
        root.render(<WarningListTable data={pv.warningData || []} />);
        return;
      }
      // 堆叠图用 stackData，其它图用 chartData
      if (chartType === 'BY_SCHOOL_STACK_CHART') {
        if (!pv.stackData || pv.stackData.length === 0) {
          container.innerHTML = '<p style="text-align:center;color:#94a3b8;padding:40px;">暂无数据</p>';
          return;
        }
        const root = createRoot(container);
        chartRootsRef.current.set(key, root);
        root.render(<ReportBySchoolStackChart data={pv.stackData} />);
        return;
      }
      if (!pv.chartData || pv.chartData.length === 0) {
        container.innerHTML = '<p style="text-align:center;color:#94a3b8;padding:40px;">暂无数据</p>';
        return;
      }
      const root = createRoot(container);
      chartRootsRef.current.set(key, root);
      if (chartType === 'PIE_CHART') root.render(<ReportPieChart data={pv.chartData} />);
      else if (chartType === 'BY_SCHOOL_BAR_CHART') root.render(<ReportBySchoolBarChart data={pv.chartData} />);
      else root.render(<ReportBarChart data={pv.chartData} />);
    });
  }, []);

  useEffect(() => {
    if (previewData) {
      requestAnimationFrame(() => renderToDom(previewData));
    }
    return () => {
      chartRootsRef.current.forEach((r) => { try { r.unmount(); } catch {} });
      chartRootsRef.current.clear();
    };
  }, [previewData, renderToDom]);

  // ---- render ----
  if (loading && !previewData) {
    return (
      <div className="bg-white rounded-3xl shadow-soft p-12 text-center text-on-surface-variant font-body">
        加载中...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-danger-50 border border-danger-200 text-danger-800 px-6 py-4 rounded-2xl">
        {error}
      </div>
    );
  }

  if (!previewData) {
    return (
      <div className="bg-white rounded-3xl shadow-soft p-12 text-center text-on-surface-variant font-body">
        {emptyHint}
      </div>
    );
  }

  const scopeLabel = classId ? '班级视图' : schoolId ? '学校视图' : '全量视图';

  return (
    <div className="bg-white rounded-3xl shadow-soft p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b">
        <h2 className="text-xl font-bold font-headline text-on-surface">
          {title ?? '报告'}
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          {!controlledScope && (
            <>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-on-surface-variant font-label">学校</span>
                <select
                  value={schoolId ?? ''}
                  onChange={(e) =>
                    onSchoolChange(e.target.value === '' ? null : Number(e.target.value))
                  }
                  disabled={loading || visibleSchools.length === 0}
                  className="h-10 px-3 bg-white border border-outline-variant rounded-xl text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50"
                >
                  {allowAllSchools && <option value="">全部学校</option>}
                  {visibleSchools.map((s) => (
                    <option key={s.schoolId} value={s.schoolId}>{s.schoolName}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-on-surface-variant font-label">班级</span>
                <select
                  value={classId ?? ''}
                  onChange={(e) => onClassChange(e.target.value === '' ? null : e.target.value)}
                  disabled={loading || schoolId === null || visibleClasses.length === 0}
                  className="h-10 px-3 bg-white border border-outline-variant rounded-xl text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50"
                >
                  {allowAllClasses && <option value="">全部班级</option>}
                  {visibleClasses.map((c) => (
                    <option key={c.classId} value={c.classId}>{c.className}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          <div className="text-xs text-on-surface-variant font-label whitespace-nowrap">
            {scopeLabel} · {previewData.placeholderValues.length} 项
          </div>
          {/* 下载：PDF / Word（前端导出，所见即所得） */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleExport('pdf')}
              disabled={exporting !== null}
              title="下载 PDF（含图表，所见即所得）"
              className="inline-flex items-center gap-1 h-10 px-3 rounded-xl text-sm font-label font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 disabled:opacity-50"
            >
              {exporting === 'pdf'
                ? <span className="animate-spin inline-block w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full" />
                : <span className="material-symbols-outlined" style={{ fontSize: 18 }}>picture_as_pdf</span>}
              PDF
            </button>
            <button
              onClick={() => handleExport('word')}
              disabled={exporting !== null}
              title="下载 Word（图表转图片内嵌）"
              className="inline-flex items-center gap-1 h-10 px-3 rounded-xl text-sm font-label font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            >
              {exporting === 'word'
                ? <span className="animate-spin inline-block w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full" />
                : <span className="material-symbols-outlined" style={{ fontSize: 18 }}>description</span>}
              Word
            </button>
          </div>
        </div>
      </div>

      <div ref={previewRef} className="prose prose-sm max-w-none report-preview-content" />

      {previewData.placeholderValues.filter(pv => pv.placeholderType === 'TEXT').length > 0 && (
        <div className="mt-8 pt-6 border-t">
          <h3 className="text-sm font-semibold text-on-surface-variant mb-3 font-label">指标解析详情</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {previewData.placeholderValues
              .filter(pv => pv.placeholderType === 'TEXT')
              .map((pv) => (
                <div key={pv.key} className="bg-blue-50 rounded-xl p-3">
                  <div className="text-xs text-blue-600 font-label">{pv.displayName}</div>
                  <div className="text-sm font-semibold text-blue-800 mt-1">{pv.resolvedValue}</div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPreviewView;
